/** 请求的 401 策略：受保护请求恢复登录态，公开认证请求不参与恢复。 */
export type AuthMode = 'required' | 'none';

interface AuthModeConfig {
  authMode?: AuthMode;
}

/** OAuth 与 nologin 接口天然公开，其余接口默认要求登录。 */
export function resolveAuthMode(config: AuthModeConfig = {}, url = ''): AuthMode {
  if (config.authMode) return config.authMode;
  if (url.includes('/oauth/') || url.includes('/nologin/')) return 'none';
  return 'required';
}

/** 本地存在任一 token 时才向服务端验证会话。 */
export function shouldVerifySession(accessToken: string | null, refreshToken: string | null): boolean {
  return Boolean(accessToken || refreshToken);
}

/** 仅登录用户且租户标识有效时加载租户资源。 */
export function shouldLoadTenantResources(isLogin: boolean, tenant: string | null | undefined): boolean {
  return Boolean(isLogin && tenant && tenant !== 'undefined' && tenant !== ':tenant');
}

/** 登录页已有本地会话时应离开登录页，避免清空其他标签页的 token。 */
export function resolveLoginPageEntryAction(accessToken: string | null, refreshToken: string | null): 'redirect' | 'stay' {
  return shouldVerifySession(accessToken, refreshToken) ? 'redirect' : 'stay';
}

/** 解析已登录离开登录页时的站内回跳路径，拒绝外链与协议相对地址。 */
export function resolveLoginRedirectPath(search = '', fallback = '/home'): string {
  const query = search.startsWith('?') ? search.slice(1) : search;
  const redirect = new URLSearchParams(query).get('redirect');
  if (!redirect) return fallback;
  try {
    const decoded = decodeURIComponent(redirect);
    if (decoded.startsWith('/') && !decoded.startsWith('//')) return decoded;
  } catch (error) {
    console.log(error);
  }
  return fallback;
}

/** OAuth 与 nologin 公共接口不携带平台 access token。 */
export function shouldAttachAccessToken(url = ''): boolean {
  return !url.includes('/oauth/') && !url.includes('/nologin/');
}

/** 401 所属会话与当前会话不同时，决定使用新 token 重试或直接忽略旧失败。 */
export function resolveStaleRequestAction(
  requestAccessToken: string | null,
  currentAccessToken: string | null,
): 'recover' | 'retry-current' | 'reject' {
  if (requestAccessToken === currentAccessToken) return 'recover';
  return currentAccessToken ? 'retry-current' : 'reject';
}

/** 优先读取业务响应 code，并兼容字符串状态码与无 response 的网络错误。 */
export function resolveResponseStatus(dataCode: unknown, responseStatus: unknown): number | undefined {
  for (const status of [dataCode, responseStatus]) {
    if (status === null || status === undefined || status === '') continue;
    const normalizedStatus = Number(status);
    if (Number.isFinite(normalizedStatus)) return normalizedStatus;
  }
  return undefined;
}

/** 403 全局处理策略：GET 替换当前页，写请求弹框提示，公开认证请求不介入。 */
export function resolveForbiddenAction(method = '', authMode: AuthMode = 'required'): 'page' | 'dialog' | 'reject' {
  if (authMode === 'none') return 'reject';
  return method.toLowerCase() === 'get' ? 'page' : 'dialog';
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

interface AuthRecoveryDependencies {
  getRefreshToken: () => string | null;
  refreshTokens: (refreshToken: string) => Promise<AuthTokens>;
  saveTokens: (tokens: AuthTokens) => void;
  invalidateSession: () => void;
}

interface RecoverRequestOptions<T> {
  mode: AuthMode;
  hasRetried: boolean;
  originalError: unknown;
  retry: () => Promise<T>;
}

interface SessionInvalidatorDependencies {
  clearSession: () => void;
  getLocation: () => { pathname: string; search: string };
  loginPath: string;
  redirect: (url: string) => void;
  notifyLoginRequired: () => void;
}

/** 创建幂等的登录态失效处理器，避免并发 401 重复跳转。 */
export function createSessionInvalidator(dependencies: SessionInvalidatorDependencies) {
  let redirecting = false;

  return () => {
    dependencies.clearSession();
    if (redirecting) return;

    redirecting = true;
    const { pathname, search } = dependencies.getLocation();
    if (pathname === dependencies.loginPath) {
      dependencies.notifyLoginRequired();
      return;
    }
    dependencies.redirect(`${dependencies.loginPath}?redirect=${encodeURIComponent(pathname + search)}`);
  };
}

/**
 * 创建认证恢复协调器。所有并发 401 共用一次刷新请求。
 */
export function createAuthRecoveryCoordinator(dependencies: AuthRecoveryDependencies) {
  let refreshTask: { token: string | null; promise: Promise<void> } | null = null;

  const createRefreshTask = () => {
    const token = dependencies.getRefreshToken();
    const refreshToken = token;
    if (!refreshToken) return Promise.reject(new Error('Refresh token not found'));

    return dependencies.refreshTokens(refreshToken).then((tokens) => {
      if (!tokens.accessToken || !tokens.refreshToken) throw new Error('Invalid refresh response');
      // 登录期间完成的旧 refresh 不能覆盖新会话 token。
      if (dependencies.getRefreshToken() !== refreshToken) return;
      dependencies.saveTokens(tokens);
    });
  };

  return async function recoverRequest<T>({ mode, hasRetried, originalError, retry }: RecoverRequestOptions<T>): Promise<T> {
    if (mode === 'none') return Promise.reject(originalError);

    // 已用新 token 重放仍 401：说明平台会话有效，属于业务/资源误报，不得整会话登出。
    if (hasRetried) {
      return Promise.reject(originalError);
    }

    if (!refreshTask) {
      const token = dependencies.getRefreshToken();
      const promise = createRefreshTask().finally(() => {
        refreshTask = null;
      });
      refreshTask = { token, promise };
    }
    const currentTask = refreshTask;

    try {
      await currentTask.promise;
    } catch (refreshError) {
      // 刷新期间若 token 已变化，说明新会话已经建立，旧失败不得清理新 token。
      if (dependencies.getRefreshToken() !== currentTask.token) return retry();
      dependencies.invalidateSession();
      return Promise.reject(refreshError);
    }

    return retry();
  };
}
